/**
 * HustleAnswerDemoPage.jsx — live interactive demo of the Hustle Ai
 * Receptionist. Reachable at /hustleanswer/demo and on
 * demo.hustleanswer.au (mapped in RootRouter).
 *
 * Runs entirely client-side against a simulated call transcript so the
 * page can render on the marketing subdomain without needing a signed-in
 * account. Every action is data-testid'd for the testing agent.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import PlatformHeader from "@/components/PlatformHeader";
import SuiteFooter from "@/components/SuiteFooter";
import { Button } from "@/components/ui/button";
import { PhoneCall, PhoneOff, Bot, User, Play, RotateCcw, Languages, ShieldCheck, MessageSquare, CheckCircle2 } from "lucide-react";

const SCRIPTS = {
  english: [
    { who: "caller",       text: "Hi, do you have a table for 4 tonight at 7:30?",             hint: "Caller books in English." },
    { who: "receptionist", text: "Good evening — thanks for calling Caffè Roma. Absolutely, let me check the diary for 7:30pm tonight, table for four…" },
    { who: "receptionist", text: "I've got you seated for 7:30pm on the terrace — under what name should I put the booking?" },
    { who: "caller",       text: "Sarah — and one of my kids is nut allergic, is that OK?" },
    { who: "receptionist", text: "Absolutely Sarah — I've flagged nut allergy on the booking, the kitchen will confirm-clean the pass before your table. You'll get an SMS with the details in a moment." },
    { who: "caller",       text: "Perfect, thanks!" },
    { who: "system",       text: "→ Booking created · 4 covers · 7:30pm · nut-allergy flag · SMS sent · CRM updated", hint: "The Ai handed off to your systems." },
  ],
  cantonese: [
    { who: "caller",       text: "喂,今晚 7:30 有無 4 位?",                                    hint: "Caller starts in Cantonese." },
    { who: "receptionist", text: "多謝致電 Caffè Roma,今晚 7:30 4 位…等我查一查 diary…" },
    { who: "receptionist", text: "7:30pm terrace 有位,請問點稱呼?" },
    { who: "caller",       text: "叫我 Wing 就得。有一個小朋友花生過敏。" },
    { who: "receptionist", text: "收到 Wing,已經標記花生過敏。廚房會先清潔工作台。SMS 確認即刻發過嚟。" },
    { who: "system",       text: "→ 預訂已完成 · 4 位 · 7:30pm · 花生過敏標記 · 已發 SMS · CRM 已更新", hint: "Language stamped Cantonese." },
  ],
  escalation: [
    { who: "caller",       text: "I'd like to speak to the manager, we had food poisoning last night.", hint: "Escalation keyword: 'food poisoning'." },
    { who: "receptionist", text: "I'm so sorry to hear that. I'm connecting you to our manager right now — please hold." },
    { who: "system",       text: "→ Ai paused · call bridged to owner's mobile · full transcript SMS'd ahead · CRM ticket #HA-1042 opened", hint: "Instant human handoff." },
  ],
};

const SCENARIOS = [
  { key: "english",    label: "Standard booking · English",    icon: MessageSquare },
  { key: "cantonese",  label: "Language switch · Cantonese",   icon: Languages },
  { key: "escalation", label: "Complaint escalation",          icon: ShieldCheck },
];

export default function HustleAnswerDemoPage() {
  const [scenario, setScenario] = useState("english");
  const [playing, setPlaying]   = useState(false);
  const [index, setIndex]       = useState(0);
  const timerRef = useRef(null);

  useEffect(() => { document.title = "HustleAnswer — Live Demo"; }, []);

  const script = useMemo(() => SCRIPTS[scenario] || [], [scenario]);

  useEffect(() => {
    if (!playing) return;
    if (index >= script.length) { setPlaying(false); return; }
    timerRef.current = setTimeout(() => setIndex((i) => i + 1), 1600);
    return () => clearTimeout(timerRef.current);
  }, [playing, index, script.length]);

  function start() {
    setIndex(0);
    setPlaying(true);
  }
  function reset() {
    setPlaying(false);
    setIndex(0);
    if (timerRef.current) clearTimeout(timerRef.current);
  }
  function pickScenario(key) {
    setScenario(key);
    reset();
  }

  return (
    <div
      data-testid="hustleanswer-demo-page"
      className="min-h-screen bg-white text-stone-900 dark:bg-[#07131a] dark:text-stone-100 transition-colors"
    >
      <PlatformHeader
        brand={{ name: "HustleAnswer", to: "/hustleanswer" }}
        nav={[
          { to: "/hustleanswer",         label: "Overview" },
          { to: "/hustleanswer/demo",    label: "Live demo" },
          { to: "/hustleanswer/app",     label: "Configure", align: "right", variant: "primary" },
        ]}
        showBookCta={false}
        showCart={false}
      />

      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-14 pb-8">
        <p className="text-xs uppercase tracking-[0.25em] font-bold text-cyan-500 mb-4">
          Live demo
        </p>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight tracking-tight max-w-3xl">
          Hear a call, watch the booking land, see the escalation logic.
        </h1>
        <p className="text-stone-600 dark:text-stone-400 mt-4 max-w-2xl">
          Pick a scenario, hit play, and watch how HustleAnswer picks up
          the phone, books the table live, flags allergens, and escalates
          the complaint calls straight to a human. No sign-up. No number
          port. Just the transcript.
        </p>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-14 grid lg:grid-cols-[280px_1fr] gap-6" data-testid="answer-demo-shell">
        {/* Scenario picker */}
        <aside
          data-testid="answer-demo-scenarios"
          className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-stone-50 dark:bg-stone-900/50 p-4 space-y-2 h-fit"
        >
          <p className="text-[10px] uppercase tracking-widest font-bold text-stone-500 dark:text-stone-500 px-2 mb-2">
            Scenarios
          </p>
          {SCENARIOS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              type="button"
              onClick={() => pickScenario(key)}
              data-testid={`answer-demo-scenario-${key}`}
              className={
                "w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-left text-sm transition-colors " +
                (scenario === key
                  ? "bg-cyan-500 text-stone-950 font-semibold"
                  : "hover:bg-stone-100 dark:hover:bg-stone-800 text-stone-800 dark:text-stone-200")
              }
            >
              <Icon className="h-4 w-4 shrink-0" strokeWidth={1.75} />
              {label}
            </button>
          ))}
          <div className="pt-3 border-t border-stone-200 dark:border-stone-800 mt-3 space-y-2">
            <Button
              onClick={start}
              disabled={playing}
              data-testid="answer-demo-play"
              className="w-full rounded-full bg-cyan-500 hover:bg-cyan-400 text-stone-950 font-semibold"
            >
              <Play className="h-4 w-4 mr-2" strokeWidth={2} />
              {playing ? "Playing…" : "Play scenario"}
            </Button>
            <Button
              onClick={reset}
              variant="outline"
              data-testid="answer-demo-reset"
              className="w-full rounded-full border-stone-300 dark:border-stone-700"
            >
              <RotateCcw className="h-4 w-4 mr-2" strokeWidth={2} />
              Reset
            </Button>
          </div>
        </aside>

        {/* Transcript */}
        <div
          data-testid="answer-demo-transcript"
          className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900/30 min-h-[420px] p-5 flex flex-col"
        >
          <div className="flex items-center gap-2 pb-3 border-b border-stone-200 dark:border-stone-800 mb-4">
            <PhoneCall className="h-4 w-4 text-cyan-500" strokeWidth={1.75} />
            <span className="text-xs font-bold uppercase tracking-widest text-stone-500 dark:text-stone-500">
              Live call · {scenario}
            </span>
            {playing && (
              <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-emerald-500">
                <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                In progress
              </span>
            )}
          </div>
          <div className="flex-1 space-y-3 overflow-y-auto">
            {script.slice(0, index).map((row, i) => (
              <TranscriptRow key={i} row={row} testid={`answer-demo-row-${i}`} />
            ))}
            {!playing && index === 0 && (
              <div className="text-sm text-stone-500 dark:text-stone-500 italic pt-8">
                Hit <em>Play scenario</em> to start the call.
              </div>
            )}
            {!playing && index > 0 && index >= script.length && (
              <div
                data-testid="answer-demo-complete"
                className="mt-4 rounded-xl border border-emerald-300 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/40 p-4 text-sm flex items-start gap-3"
              >
                <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" strokeWidth={1.75} />
                <div>
                  <p className="font-bold text-stone-900 dark:text-stone-100">
                    Call complete
                  </p>
                  <p className="text-stone-700 dark:text-stone-300 mt-1">
                    Ready to try another scenario? Pick one on the left, or
                    {" "}
                    <Link to="/hustleanswer/app" className="text-cyan-600 dark:text-cyan-400 font-semibold underline">configure your own</Link>.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <SuiteFooter />
    </div>
  );
}

function TranscriptRow({ row, testid }) {
  const isCaller       = row.who === "caller";
  const isReceptionist = row.who === "receptionist";
  const isSystem       = row.who === "system";
  const rowStyle = isSystem
    ? "bg-stone-100 dark:bg-stone-800/60 text-stone-700 dark:text-stone-300 text-xs italic"
    : isReceptionist
    ? "bg-cyan-50 dark:bg-cyan-950/30 text-stone-900 dark:text-stone-100"
    : "bg-white dark:bg-stone-900/60 text-stone-900 dark:text-stone-100";

  const Icon = isSystem ? PhoneOff : isReceptionist ? Bot : User;
  const label = isSystem ? "System" : isReceptionist ? "HustleAnswer" : "Caller";
  return (
    <div data-testid={testid} className={`rounded-xl p-3 border border-stone-200 dark:border-stone-800 ${rowStyle}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="h-3.5 w-3.5" strokeWidth={1.75} />
        <span className="text-[10px] font-bold uppercase tracking-widest">{label}</span>
      </div>
      <p className="text-sm leading-relaxed">{row.text}</p>
      {row.hint && (
        <p className="text-[11px] text-stone-500 dark:text-stone-500 mt-1 italic">
          {row.hint}
        </p>
      )}
    </div>
  );
}
